<script lang="ts" context="module">
  export type TextSize = 'normal' | 'dek' | 'caption' | 'breadcrumb';
  export type TextKey = 'paragraph' | 'dek' | 'caption' | 'breadcrumb';

  const DEFAULT_TEXT_FONT_SIZES: Record<TextKey, [number, number]> = {
    paragraph: [12, 18],
    dek: [16, 24],
    caption: [10, 15],
    breadcrumb: [12, 15]
  };

  const DEFAULT_TEXT_FONT_SIZES_RESKIN: Record<TextKey, [number, number]> = {
    dek: [18, 20], // matches p1
    paragraph: [16, 16], // matches p2
    caption: [14, 14], // matches p3
    breadcrumb: [12, 12]
  };
</script>

<script lang="ts">
  import type { PageCollectionTheme } from '$lib/sanity/schemas';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import { cx } from '@emotion/css';
  import { ResponsiveVars, type FluidProp } from '@twist/core';
  import { getContext } from 'svelte';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';

  export let size: TextSize = 'normal';

  const key: TextKey = size === 'normal' ? 'paragraph' : size;
  const theme = getContext<PageCollectionTheme>('theme');
  const themeMobile = theme?.text?.mobile?.[key];
  const themeDesktop = theme?.text?.desktop?.[key];
  let fontSizeVarInput: FluidProp;
  let letterSpacingVarInput: FluidProp;

  $: brandReskinEnabled = flagValue(FEATURE_FLAGS.BRAND_RESKIN);

  $: {
    const [defaultMobile, defaultDesktop] = brandReskinEnabled
      ? DEFAULT_TEXT_FONT_SIZES_RESKIN[key]
      : DEFAULT_TEXT_FONT_SIZES[key];
    fontSizeVarInput = toFluidVarInput(
      parseInt(themeMobile?.fontSize ?? '') || defaultMobile,
      parseInt(themeDesktop?.fontSize ?? '') || defaultDesktop
    );
    const defaultLetterSpacing = key === 'breadcrumb' ? '1' : '0';
    letterSpacingVarInput = toFluidVarInput(
      parseInt(themeMobile?.letterSpacing ?? defaultLetterSpacing),
      parseInt(themeDesktop?.letterSpacing ?? defaultLetterSpacing)
    );
  }

  $: responsiveVars = new ResponsiveVars()
    .addFluid('font-size', fontSizeVarInput)
    .addFluid('letter-spacing', letterSpacingVarInput)
    .addResponsive('theme-text-color', {
      min: themeMobile?.color,
      lg: themeDesktop?.color
    })
    .addResponsive('theme-line-height', {
      min: themeMobile?.lineHeight,
      lg: themeDesktop?.lineHeight
    })
    .addResponsive('theme-font-family', {
      min: themeMobile?.fontFamily,
      lg: themeDesktop?.fontFamily
    })
    .addResponsive('theme-font-weight', {
      min: themeMobile?.fontWeight,
      lg: themeDesktop?.fontWeight
    })
    .addResponsive('theme-text-transform', {
      min: themeMobile?.textTransform,
      lg: themeDesktop?.textTransform
    })
    .toCSS();
</script>

<span class={cx(responsiveVars, size, { reskin: brandReskinEnabled })}>
  <slot />
</span>

<style lang="scss">
  @import 'src/scss/media';

  .normal {
    --default-font-weight: 400;
    --default-line-height: 140%;
    --default-font-family: var(--font-family-primary);
    --default-text-transform: none;
  }

  .dek {
    --default-font-weight: 500;
    --default-line-height: 125%;
    --default-font-family: var(--font-family-primary);
    --default-text-transform: none;

    &.reskin {
      --default-line-height: 140%;
      --default-font-weight: 400;
    }
  }

  .caption {
    --default-font-weight: 400;
    --default-line-height: 120%;
    --default-font-family: var(--font-family-primary);
    --default-text-transform: none;

    &.reskin {
      --default-line-height: 140%;
    }
  }

  .breadcrumb {
    --default-font-weight: 600;
    --default-line-height: 120%;
    --default-font-family: var(--font-family-primary);
    --default-text-transform: uppercase;

    &.reskin {
      --default-line-height: 130%;
      --default-font-weight: 400;
    }
  }

  span {
    display: contents;
    font-size: var(--font-size);
    letter-spacing: var(--letter-spacing);
    line-height: var(--theme-line-height, var(--default-line-height));
    font-family: var(--theme-font-family, var(--default-font-family));
    font-weight: var(--theme-font-weight, var(--default-font-weight));
    text-transform: var(--theme-text-transform, var(--default-text-transform));
    color: var(--theme-text-color, var(--theme-general-foreground-color, inherit));

    &.reskin {
      font-family: var(--default-font-family);
    }
  }
</style>
